import React, { useEffect, useState } from "react";
import { Button, Card, Input, Space, Spin, Typography } from "antd";
import { KeyOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { fetchLicenseStatus, activateLicense } from "../redux/slieces/licenseSlice";
import notificationBus from "../events/notificationBus";

dayjs.extend(utc);

const formatValidUntil = (validUntil, language) => {
    if (!validUntil) return "";
    const d = dayjs.utc(validUntil);
    if (!d.isValid()) return String(validUntil);
    const fmt = (language || "").toLowerCase().startsWith("de") ? "DD.MM.YYYY" : "MMM D, YYYY";
    return d.format(fmt);
};

const renderStatusText = (status, t, i18n) => {
    if (!status) return "";
    const validUntilFormatted = formatValidUntil(status.validUntil, i18n.language);
    switch (status.status) {
        case "Ok":
            return t("license.status.ok", {
                current: status.currentUsers,
                max: status.maxUsers,
                validUntil: validUntilFormatted,
            });
        case "Expired":
            return t("license.status.expired", { validUntil: validUntilFormatted });
        case "LimitReached":
            return t("license.status.limitReached", { max: status.maxUsers });
        case "NoLicense":
            return t("license.status.noLicense");
        default:
            return t("license.status.unknown", { value: String(status.status) });
    }
};

const LicenseSection = () => {
    const { t, i18n } = useTranslation();
    const { keycloak } = useKeycloak();
    const dispatch = useDispatch();
    const { status, loading } = useSelector((state) => state.license);

    const [licenseKey, setLicenseKey] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const isAdmin = keycloak?.tokenParsed?.realm_access?.roles?.includes("mediadmin");

    useEffect(() => {
        let alive = true;
        dispatch(fetchLicenseStatus())
            .unwrap()
            .catch(() => {
                if (!alive) return;
                notificationBus.emit("notify", {
                    type: "error",
                    message: t("license.toast.loadError"),
                });
            });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleActivate = async () => {
        if (submitting) return;
        const trimmed = licenseKey.trim();
        if (!trimmed) return;
        setSubmitting(true);
        try {
            await dispatch(activateLicense(trimmed)).unwrap();
            setLicenseKey("");
            notificationBus.emit("notify", {
                type: "success",
                message: t("license.toast.activateSuccess"),
            });
        } catch (errorPayload) {
            const reason = errorPayload?.reason;
            let message;
            if (reason === "WrongEmail") {
                message = t("license.toast.activateError.wrongEmail");
            } else if (reason === "InvalidKey") {
                message = t("license.toast.activateError.invalidKey");
            } else {
                message = t("license.toast.activateError.generic");
            }
            notificationBus.emit("notify", {
                type: "error",
                message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card
            data-testid="license-section-card"
            title={
                <Space>
                    <KeyOutlined />
                    <span>{t("license.section.title")}</span>
                </Space>
            }
            style={{ marginTop: 16 }}
        >
            {loading && !status ? (
                <Spin size="small" />
            ) : (
                <p data-testid="license-status-text" style={{ marginBottom: 16 }}>
                    <strong>{t("license.section.currentStatus")}: </strong>
                    <span>{renderStatusText(status, t, i18n)}</span>
                </p>
            )}

            {isAdmin && keycloak?.tokenParsed?.email && (
                // The license backend (LicenseService.validateAndDecode)
                // binds each .lic file to the admin's account email — admins
                // need to know exactly which address to share when ordering
                // a license.
                <div
                    data-testid="license-admin-email"
                    style={{
                        marginBottom: 16,
                        padding: 12,
                        background: "var(--bg-secondary, #fafafa)",
                        borderRadius: 8,
                    }}
                >
                    <Typography.Text type="secondary" style={{ display: "block" }}>
                        {t("license.admin.emailLabel")}
                    </Typography.Text>
                    <Typography.Text
                        strong
                        copyable
                        data-testid="license-admin-email-value"
                    >
                        {keycloak.tokenParsed.email}
                    </Typography.Text>
                    <Typography.Paragraph
                        type="secondary"
                        style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}
                    >
                        {t("license.admin.emailHelp")}
                    </Typography.Paragraph>
                </div>
            )}

            {isAdmin && (
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Input.TextArea
                        data-testid="license-key-input"
                        value={licenseKey}
                        onChange={(e) => setLicenseKey(e.target.value)}
                        placeholder={t("license.section.placeholder")}
                        rows={4}
                        disabled={submitting}
                    />
                    <Button
                        data-testid="license-activate-button"
                        type="primary"
                        onClick={handleActivate}
                        loading={submitting}
                        disabled={!licenseKey.trim() || submitting}
                    >
                        {t("license.section.activate")}
                    </Button>
                </Space>
            )}
        </Card>
    );
};

export default LicenseSection;
